import { Button, Col, Input, Row, Space, Typography } from "antd";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { API_METHODS, BLANK_VALUE } from "../../../constants";
import {
  displayFormattedDate,
  formatCurrency,
  isEmpty,
} from "../../../generalFunctions";
import useApi from "../../../hooks/useApi";
import TableComponent from "../../common/table/TableComponent";
import { EditOutlined, EyeOutlined } from "@ant-design/icons";

function PublicationBillTable() {
  const columns = [
    {
      title: "Entry Number",
      dataIndex: "id",
      width: "100px",
    },
    {
      title: "Entry Date",
      dataIndex: "entryDate",
      render: (text) => displayFormattedDate(text),
    },
    {
      title: "Bill Date",
      dataIndex: "billDate",
      render: (text) => displayFormattedDate(text),
    },
    {
      title: "Publication",
      dataIndex: "publication",
    },
    {
      title: "Bill Number",
      dataIndex: "billNumber",
    },
    {
      title: "Bill Amount",
      dataIndex: "billAmount",
      render: (text) => formatCurrency(text),
    },
    {
      title: "Action",
      key: "action",

      render: (text, record) => (
        <Space>
          <Button size="small" onClick={() => editRecord(record)}>
            <EditOutlined />
          </Button>
          <Button size="small" onClick={() => editRecord(record, true)}>
            <EyeOutlined />
          </Button>
        </Space>
      ),
    },
  ];

  const [responseData, getRequest] = useApi(
    "/publicationBill",
    null,
    API_METHODS.GET
  );

  const [billsSearchByReleaseOrderId, getBillsSearchByReleaseOrderId] = useApi(
    "/publicationBill/searchByReleaseOrder",
    null,
    API_METHODS.GET
  );

  const [billsSearchByBillNumber, getBillsSearchByBillNumber] = useApi(
    "/publicationBill/searchByBillNumber",
    null,
    API_METHODS.GET
  );

  const [tableData, setTableData] = useState();
  const [selectedRowKeys, setSelectedRowKeys] = useState([]);
  const [selectedRows, setSelectedRows] = useState([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [isSearched, setIsSearched] = useState(false);

  const navigate = useNavigate();

  useEffect(() => {
    getRequest(null, API_METHODS.GET, `/publicationBill?pageNumber=${1}`);
  }, []);

  useEffect(() => {
    const data = responseData.data !== BLANK_VALUE ? responseData.data : [];
    setTableData(data);
    setTotalRecords(data && data.length > 0 ? data[0].count : 0);
  }, [responseData]);

  useEffect(() => {
    const data =
      billsSearchByReleaseOrderId.data !== BLANK_VALUE
        ? billsSearchByReleaseOrderId.data
        : [];
    setTableData(data);
  }, [billsSearchByReleaseOrderId]);

  useEffect(() => {
    const data =
      billsSearchByBillNumber.data !== BLANK_VALUE
        ? billsSearchByBillNumber.data
        : [];
    setTableData(data);
  }, [billsSearchByBillNumber]);

  const editRecord = (record, isViewOnly = false) => {
    if (isViewOnly) {
      navigate("/portal/publicationBill/view", {
        state: { bill_id: record.id, isViewOnly: isViewOnly },
      });
    } else {
      navigate("/portal/publicationBill/edit", {
        state: { bill_id: record.id, isViewOnly: isViewOnly },
      });
    }
  };

  const onRowSelectChange = (newSelectedRowKeys, records) => {
    setSelectedRowKeys(newSelectedRowKeys);
    setSelectedRows(records);
  };

  const rowSelection = {
    selectedRowKeys,
    type: "checkbox",
    onChange: onRowSelectChange,
  };

  const onReleaseOrderSearch = (roId) => {
    if (!isEmpty(roId)) {
      setIsSearched(true);
      getBillsSearchByReleaseOrderId(
        null,
        API_METHODS.GET,
        `/publicationBill/searchByReleaseOrder/${roId}`
      );
    } else {
      setIsSearched(false);
      getRequest(
        null,
        API_METHODS.GET,
        `/publicationBill?pageNumber=${currentPage}`
      );
    }
  };

  const onBillSearch = (billNumber) => {
    if (!isEmpty(billNumber)) {
      setIsSearched(true);
      getBillsSearchByBillNumber(
        null,
        API_METHODS.GET,
        `/publicationBill/searchByBillNumber/${billNumber}`
      );
    } else {
      setIsSearched(false);
      getRequest(
        null,
        API_METHODS.GET,
        `/publicationBill?pageNumber=${currentPage}`
      );
    }
  };

  const onPaginationChange = (page) => {
    getRequest(
      null,
      API_METHODS.GET,
      `/publicationBill?pageNumber=${page.current}`
    );
    setCurrentPage(page.current);
  };

  return (
    <>
      <Row>
        <Col span={6}>
          <Typography.Title level={5}>Publication Bills</Typography.Title>
        </Col>
        <Col span={18}>
          <Space style={{ marginBottom: 10, float: "right" }}>
            <Input.Search
              prefix="RO-"
              placeholder="Release Order Number"
              allowClear
              enterButton="Search"
              onSearch={onReleaseOrderSearch}
            />
            <Input.Search
              placeholder="Bill Number"
              allowClear
              enterButton="Search"
              onSearch={onBillSearch}
            />

            <Button>
              <Link to="add">Add Publication Bill</Link>
            </Button>
          </Space>
        </Col>
      </Row>
      {!isSearched && (
        <TableComponent
          columns={columns}
          tableData={tableData}
          loading={responseData.isLoading}
          rowSelection={rowSelection}
          onPaginationChange={onPaginationChange}
          total={totalRecords}
          current={currentPage}
        />
      )}
      {isSearched && (
        <TableComponent
          columns={columns}
          tableData={tableData}
          clientSideRender={true}
        />
      )}
    </>
  );
}

export default PublicationBillTable;
